import { apiClient, StreamCompleteData, StreamStartData } from './apiClient';
import { API_ENDPOINTS } from '../config/api';
import { Message, ChatSession } from '../types';

export interface SendMessageRequest {
    message: string;
    sessionId?: string | null;
    fileIds?: string[];
}

export interface CreateSessionRequest {
    sessionName?: string;
}

export interface SendMessageResponse {
    session_id: string;
    message_id: string;
    user_message: string;
    ai_response: string;
    timestamp: string;
    tokens_used: number;
    response_time: number;
    session_name?: string;  // 添加会话标题字段
    files?: any[];
}

export interface StreamChatRequest {
    message: string;
    session_id?: string | null;
    file_ids?: string[];
}

class ChatService {
    // 2026-09-18 用户令：消息反馈（沿用 Kevin Message.feedback_text/liked 设施）
    async submitFeedback(messageId: string, payload: { liked?: boolean | null; feedback_text?: string },
                         authString?: string): Promise<{ message_id?: string }> {
        const r = await apiClient.post<{ message_id?: string }>(
            API_ENDPOINTS.CHAT.MESSAGE_FEEDBACK(messageId), payload, authString);
        return r.data || {};
    }
    async submitFeedbackByContent(
        payload: { session_id: string; match_text: string; liked?: boolean | null; feedback_text?: string },
        authString?: string
    ): Promise<{ message_id?: string }> {
        const r = await apiClient.post<{ message_id?: string }>(
            API_ENDPOINTS.CHAT.MESSAGE_FEEDBACK_BY_CONTENT, payload, authString);
        return r.data || {};
    }

    async createSession(
        request: CreateSessionRequest,
        authString?: string
    ): Promise<ChatSession> {
        const response = await apiClient.post<ChatSession>(
            API_ENDPOINTS.CHAT.SESSIONS,
            {
                session_name: request.sessionName || '新对话',
            },
            authString
        );

        if (response.error) {
            throw new Error(response.error);
        }

        return response.data!;
    }

    async getChatSessions(authString?: string): Promise<ChatSession[]> {
        // 2026-09-24：后端 DRF 全局 PAGE_SIZE=20 分页，原实现只取第 1 页
        // → 侧栏只见 20 条（test 账户 74 条实证，count/next 字段齐全）。
        // 翻页拉全，next 为空即止；50 页上限防异常死循环。
        const all: ChatSession[] = [];
        for (let page = 1; page <= 50; page++) {
            const response = await apiClient.get<{ results: ChatSession[]; next: string | null }>(
                `${API_ENDPOINTS.CHAT.SESSIONS}?page=${page}`,
                authString
            );
            if (response.error) {
                throw new Error(response.error);
            }
            all.push(...(response.data?.results || []));
            if (!response.data?.next) break;
        }
        return all;
    }

    async getSessionMessages(
        sessionId: string,
        authString?: string
    ): Promise<Message[]> {
        const response = await apiClient.get<Message[]>(
            API_ENDPOINTS.CHAT.SESSION_MESSAGES(sessionId),
            authString
        );

        if (response.error) {
            throw new Error(response.error);
        }

        return response.data || [];
    }

    async deleteSession(sessionId: string, authString?: string): Promise<void> {
        const response = await apiClient.delete(
            API_ENDPOINTS.CHAT.DELETE_SESSION(sessionId),
            authString
        );

        if (response.error) {
            throw new Error(response.error);
        }
    }

    /** 重命名会话（2026-09-17：后端 ModelViewSet partial_update 原生支持 session_name） */
    async renameSession(sessionId: string, sessionName: string, authString?: string): Promise<void> {
        const response = await apiClient.patch(
            API_ENDPOINTS.CHAT.DELETE_SESSION(sessionId),  // 同 Detail URL，PATCH 即改名
            { session_name: sessionName },
            authString
        );
        if (response.error) {
            throw new Error(response.error);
        }
    }

    async sendStreamMessage(
        request: StreamChatRequest,
        authString: string,
        onChunk: (chunk: string) => void,
        onStart?: (data: StreamStartData) => void,
        onComplete?: (data: StreamCompleteData) => void
    ): Promise<void> {
        console.log('🚀 开始流式请求:', request);
        const result = await apiClient.streamRequest(
            API_ENDPOINTS.CHAT.STREAM,
            {
                message: request.message,
                session_id: request.session_id,
                file_ids: request.file_ids || [],
            },
            authString,
            onChunk,
            onStart,
            onComplete,
        );
        console.log('✅ 流式请求完成:', result);
        
        if (result.error) {
            throw new Error(result.error);
        }
    }
}

export const chatService = new ChatService();